import type { EmailAddress, NewContact, PhoneNumber } from '@personical/cc-shared-types';

const EMAIL_RE = /\b[\w.+-]+@[\w-]+(?:\.[\w-]+)+\b/g;
const PHONE_RE = /(?:\+\d{1,3}[\s.\-()]*)?(?:\d[\s.\-()]?){7,}\d/g;
const URL_RE = /\bhttps?:\/\/[^\s]+|\bwww\.[^\s]+/g;

const COMPANY_HINTS = /\b(ltd|llc|inc|gmbh|s\.?a\.?|plc|limited|company|corp|corporation)\b/i;
const TITLE_HINTS = /\b(ceo|cto|cfo|coo|director|manager|head of|engineer|consultant|founder|owner|partner|sales|developer|designer|architect|analyst|coordinator|specialist)\b/i;

/**
 * Parse arbitrary pasted text — typically a signature block or business
 * card OCR result — into a NewContact-shaped suggestion. Best-effort, the
 * user always verifies in the form.
 */
export function parsePastedContact(text: string): Partial<NewContact> {
  const original = text.trim();
  if (!original) return {};

  const emails: EmailAddress[] = [];
  const phones: PhoneNumber[] = [];
  let website: string | undefined;

  const emailMatches = original.match(EMAIL_RE) ?? [];
  for (const m of emailMatches) {
    const lower = m.toLowerCase();
    if (!emails.some((e) => e.address.toLowerCase() === lower)) {
      emails.push({ type: 'work', address: m });
    }
  }

  // Strip emails before phone match so digits in addresses (e.g. +1@x.com)
  // don't get mis-detected. URL strip too.
  const stripped = original.replace(EMAIL_RE, ' ').replace(URL_RE, ' ');
  const phoneMatches = stripped.match(PHONE_RE) ?? [];
  for (const raw of phoneMatches) {
    const cleaned = raw.trim().replace(/\s+/g, ' ');
    const digits = cleaned.replace(/\D+/g, '');
    if (digits.length < 7) continue;
    if (phones.some((p) => p.number.replace(/\D+/g, '') === digits)) continue;
    phones.push({ type: 'mobile', number: cleaned });
  }

  const urlMatch = original.match(URL_RE);
  if (urlMatch) website = urlMatch[0];

  // Guess name + company + jobTitle from non-noise lines.
  const noise = new Set<string>([
    ...emailMatches,
    ...phoneMatches,
    ...(urlMatch ?? [])
  ]);
  const lines = original
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((l) => ![...noise].some((n) => l.includes(n)));

  let displayName: string | undefined;
  let jobTitle: string | undefined;
  let company: string | undefined;

  for (const line of lines) {
    if (!displayName && /^[A-Z][\w'.-]*(?:\s+[A-Z][\w'.-]*)+/.test(line)) {
      // Looks like a Title-Cased name with at least two words.
      displayName = line;
      continue;
    }
    if (!jobTitle && TITLE_HINTS.test(line)) {
      jobTitle = line;
      continue;
    }
    if (!company && COMPANY_HINTS.test(line)) {
      company = line;
      continue;
    }
  }

  if (!displayName && lines[0]) displayName = lines[0];
  if (!company && lines[1] && lines[1] !== displayName && lines[1] !== jobTitle) {
    company = lines[1];
  }

  return {
    displayName,
    jobTitle,
    company,
    website,
    emails,
    phoneNumbers: phones
  };
}
